import React from 'react';

/**
 * OptimizedImage - Performance-optimized image component
 * Features:
 * - Lazy loading by default
 * - Responsive srcset support
 * - Loading skeleton/blur effect
 * - Error handling
 */
const OptimizedImage = ({
  src,
  alt = '',
  className = '',
  loading = 'lazy',
  quality = 80,
  width,
  height,
  sizes,
  priority = false,
  onLoad,
  onError,
  fallback = null,
  ...props
}) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  const handleLoad = (e) => {
    setImageLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e) => {
    setImageError(true);
    onError?.(e);
  };

  // If error and fallback provided
  if (imageError && fallback) {
    return <>{fallback}</>;
  }

  // If error without fallback, show placeholder
  if (imageError) {
    return (
      <div className={`bg-gray-200 flex items-center justify-center ${className}`}>
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${!imageLoaded ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
      loading={priority ? 'eager' : loading}
      width={width}
      height={height}
      sizes={sizes}
      onLoad={handleLoad}
      onError={handleError}
      decoding="async"
      {...props}
    />
  );
};

export default OptimizedImage;
